import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { CreditCard, Languages, RotateCw, Save, Trash2, Upload } from 'lucide-react';
import {
  connectRevenueCat,
  createRevenueCatFullSetupJob,
  createRevenueCatProductSyncJob,
  createRevenueCatTranslateJob,
  createRevenueCatUploadJob,
  deleteRevenueCatPaywall,
  disconnectRevenueCat,
  ensureRevenueCatApps,
  getJob,
  getRevenueCatCatalog,
  getRevenueCatPaywall,
  listRevenueCatPaywalls,
  saveRevenueCatPaywall,
  syncRevenueCatPaywalls,
  triggerJob,
  type Job,
  type RevenueCatCatalogStatus,
  type RevenueCatPaywall,
  type RevenueCatPaywallSummary,
} from '../api/client';
import { AppError } from '../api/request';
import { useApplication } from '../context/ApplicationContext';
import { useError } from '../context/ErrorContext';
import { useIsPro } from '../hooks/useIsPro';
import { panelPath } from '../config/paths';
import AppPageLayout from './AppPageLayout';
import PageToolbar from './PageToolbar';
import LocaleSelectMenu from './LocaleSelectMenu';
import ModalLayout from './ModalLayout';
import ConfirmDialog from './ConfirmDialog';
import ProTag from './ProTag';
import SourceTargetLocalePicker from './SourceTargetLocalePicker';
import {
  DEFAULT_APP_STORE_LOCALE,
  REVENUECAT_PAYWALL_LOCALIZATIONS,
  isRevenueCatPaywallLocale,
} from '../constants/appStoreLocalizations';

function filterRevenueCatLocales(locales: string[]): string[] {
  return locales.filter(l => isRevenueCatPaywallLocale(l));
}

/** Dirty-check için kaydedilebilir alanların sabit snapshot'ı. */
function serializeRevenueCatSaveState(p: RevenueCatPaywall): string {
  return JSON.stringify({
    primaryLocale: p.primaryLocale,
    targetLocales: [...p.targetLocales].sort(),
    entries: p.entries.map(e => ({
      key: e.key,
      translate: e.translate !== false,
      translations: e.translations ?? {},
    })),
  });
}

type RevenueCatTab = 'catalog' | 'paywalls';

export default function RevenueCatPage() {
  const { appId } = useParams<{ appId: string }>();
  const { t } = useTranslation();
  const { showError } = useError();
  const { isPro, goToUpgrade } = useIsPro();
  // ApplicationProvider already loads GET /applications/:id for sidebar; reuse it.
  const { app: contextApp, loading, error: contextError, refetch } = useApplication();

  const app = contextApp?.type === 'mobil_app' ? contextApp : null;
  const error =
    contextError ??
    (contextApp && contextApp.type !== 'mobil_app' ? t('revenueCat.mobilOnly') : null);

  const [activeTab, setActiveTab] = useState<RevenueCatTab>('catalog');

  // Connect: app ekranında API key + Project ID girilir; backend app'e özel secret oluşturur.
  const [apiKey, setApiKey] = useState('');
  const [projectId, setProjectId] = useState('');
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [disconnectConfirmOpen, setDisconnectConfirmOpen] = useState(false);

  // Catalog (apps + iOS products)
  const [catalog, setCatalog] = useState<RevenueCatCatalogStatus | null>(null);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [ensuringApps, setEnsuringApps] = useState(false);
  const [activeProductSyncJob, setActiveProductSyncJob] = useState<Job | null>(null);
  const productSyncJobPrevStatusRef = useRef<string | null>(null);
  const [activeFullSetupJob, setActiveFullSetupJob] = useState<Job | null>(null);
  const fullSetupJobPrevStatusRef = useRef<string | null>(null);

  // Local paywall list + selection
  const [paywalls, setPaywalls] = useState<RevenueCatPaywallSummary[]>([]);
  const [selectedPaywallId, setSelectedPaywallId] = useState<string | null>(null);
  const [paywall, setPaywall] = useState<RevenueCatPaywall | null>(null);
  const [loadingPaywall, setLoadingPaywall] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Otomatik senkron: aktif paywall(lar)ı RevenueCat'ten hemen çeker (günlük koşuyu beklemeden).
  const [syncing, setSyncing] = useState(false);

  // Locales modal
  const [localesModalOpen, setLocalesModalOpen] = useState(false);
  const [primaryLocaleDraft, setPrimaryLocaleDraft] = useState('en-US');
  const [targetLocalesDraft, setTargetLocalesDraft] = useState<string[]>([]);
  const [savingLocales, setSavingLocales] = useState(false);

  const [currentEditingLocale, setCurrentEditingLocale] = useState('en-US');
  const [entryDrafts, setEntryDrafts] = useState<Record<string, string>>({});
  /** Son başarılı load/save sonrası durum; toolbar Save dirty kontrolü. */
  const [savedBaseline, setSavedBaseline] = useState<string | null>(null);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');

  const [activeTranslationJob, setActiveTranslationJob] = useState<Job | null>(null);
  const [activeUploadJob, setActiveUploadJob] = useState<Job | null>(null);
  const translationJobPrevStatusRef = useRef<string | null>(null);
  const uploadJobPrevStatusRef = useRef<string | null>(null);

  // Connected: secret yeterli; project Full Setup ile oluşturulabilir.
  const connected = Boolean(app?.revenueCatSecretId);

  const loadCatalog = useCallback(async () => {
    if (!appId) return;
    setCatalogLoading(true);
    try {
      const data = await getRevenueCatCatalog(appId);
      setCatalog(data);
    } catch (e) {
      showError(e instanceof AppError ? e.userMessage : t('revenueCat.catalogLoadError'));
    } finally {
      setCatalogLoading(false);
    }
  }, [appId, showError, t]);

  const loadPaywalls = useCallback(async () => {
    if (!appId) return;
    try {
      const items = await listRevenueCatPaywalls(appId);
      setPaywalls(items);
      if (items.length > 0 && !items.some(p => p.id === selectedPaywallId)) {
        setSelectedPaywallId(items[0].id);
      } else if (items.length === 0) {
        setSelectedPaywallId(null);
      }
    } catch (e) {
      showError(e instanceof AppError ? e.userMessage : t('revenueCat.listError'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appId, showError, t]);

  useEffect(() => {
    if (connected) void loadPaywalls();
  }, [connected, loadPaywalls]);

  useEffect(() => {
    if (connected) void loadCatalog();
  }, [connected, loadCatalog]);

  const handleEnsureApps = async () => {
    if (!appId) return;
    setEnsuringApps(true);
    try {
      await ensureRevenueCatApps(appId);
      await refetch();
      await loadCatalog();
    } catch (e) {
      showError(e instanceof AppError ? e.userMessage : t('revenueCat.ensureAppsError'));
    } finally {
      setEnsuringApps(false);
    }
  };

  const handleSyncProducts = async () => {
    if (!appId) return;
    try {
      const { jobId } = await createRevenueCatProductSyncJob(appId);
      await triggerJob(jobId);
      const job = await getJob(jobId);
      setActiveProductSyncJob(job);
      productSyncJobPrevStatusRef.current = job.status;
    } catch (e) {
      showError(e instanceof AppError ? e.userMessage : t('revenueCat.productSyncError'));
    }
  };

  const handleFullSetup = async () => {
    if (!appId) return;
    try {
      const { jobId } = await createRevenueCatFullSetupJob(appId);
      await triggerJob(jobId);
      const job = await getJob(jobId);
      setActiveFullSetupJob(job);
      fullSetupJobPrevStatusRef.current = job.status;
    } catch (e) {
      showError(e instanceof AppError ? e.userMessage : t('revenueCat.fullSetupError'));
    }
  };

  const isProductSyncing =
    activeProductSyncJob?.type === 'revenuecat_product_sync' &&
    (activeProductSyncJob.status === 'pending' || activeProductSyncJob.status === 'processing');

  const isFullSetupRunning =
    activeFullSetupJob?.type === 'revenuecat_full_setup' &&
    (activeFullSetupJob.status === 'pending' || activeFullSetupJob.status === 'processing');

  useEffect(() => {
    if (!activeProductSyncJob?.id || !isProductSyncing) return;
    const id = activeProductSyncJob.id;
    const tick = async () => {
      try {
        const job = await getJob(id);
        setActiveProductSyncJob(job);
        const prev = productSyncJobPrevStatusRef.current;
        productSyncJobPrevStatusRef.current = job.status;
        if (prev !== 'completed' && job.status === 'completed') {
          void loadCatalog();
          void refetch();
        }
        if (prev !== 'failed' && job.status === 'failed') {
          showError(job.errorMessage || t('revenueCat.productSyncError'));
          void loadCatalog();
        }
      } catch {
        /* ignore poll errors */
      }
    };
    const timer = window.setInterval(() => void tick(), 1500);
    return () => window.clearInterval(timer);
  }, [activeProductSyncJob?.id, isProductSyncing, loadCatalog, refetch, showError, t]);

  useEffect(() => {
    if (!activeFullSetupJob?.id || !isFullSetupRunning) return;
    const id = activeFullSetupJob.id;
    const tick = async () => {
      try {
        const job = await getJob(id);
        setActiveFullSetupJob(job);
        const prev = fullSetupJobPrevStatusRef.current;
        fullSetupJobPrevStatusRef.current = job.status;
        if (prev !== 'completed' && job.status === 'completed') {
          void loadCatalog();
          void refetch();
        }
        if (prev !== 'failed' && job.status === 'failed') {
          showError(job.errorMessage || t('revenueCat.fullSetupError'));
          void loadCatalog();
          void refetch();
        }
      } catch {
        /* ignore poll errors */
      }
    };
    const timer = window.setInterval(() => void tick(), 1500);
    return () => window.clearInterval(timer);
  }, [activeFullSetupJob?.id, isFullSetupRunning, loadCatalog, refetch, showError, t]);

  const loadPaywallDetail = useCallback(async (id: string) => {
    if (!appId) return;
    setLoadingPaywall(true);
    try {
      const p = await getRevenueCatPaywall(appId, id);
      const primary = isRevenueCatPaywallLocale(p.primaryLocale)
        ? p.primaryLocale
        : DEFAULT_APP_STORE_LOCALE;
      const targets = filterRevenueCatLocales(p.targetLocales || []).filter(l => l !== primary);
      const next: RevenueCatPaywall = { ...p, primaryLocale: primary, targetLocales: targets };
      setPaywall(next);
      setSavedBaseline(serializeRevenueCatSaveState(next));
      setSaveStatus('idle');
      setPrimaryLocaleDraft(primary);
      setTargetLocalesDraft(targets);
      setCurrentEditingLocale(primary);
      const drafts: Record<string, string> = {};
      for (const entry of next.entries) {
        drafts[entry.key] = entry.sourceText;
      }
      setEntryDrafts(drafts);
    } catch (e) {
      showError(e instanceof AppError ? e.userMessage : t('revenueCat.loadError'));
      setPaywall(null);
      setSavedBaseline(null);
    } finally {
      setLoadingPaywall(false);
    }
  }, [appId, showError, t]);

  useEffect(() => {
    if (selectedPaywallId) void loadPaywallDetail(selectedPaywallId);
    else {
      setPaywall(null);
      setSavedBaseline(null);
    }
  }, [selectedPaywallId, loadPaywallDetail]);

  // Dil değişince (veya paywall yeniden yüklenince) taslakları o dilin paywall verisinden doldur.
  // paywall.entries her tuşta güncellenir; dependency'ye koyma — imleç/loop olmasın.
  useEffect(() => {
    if (!paywall) return;
    const drafts: Record<string, string> = {};
    for (const entry of paywall.entries) {
      drafts[entry.key] =
        currentEditingLocale === paywall.primaryLocale
          ? entry.sourceText
          : entry.translations?.[currentEditingLocale] ?? '';
    }
    setEntryDrafts(drafts);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only resync on locale / identity refresh
  }, [currentEditingLocale, paywall?.id, paywall?.updatedAt, paywall?.primaryLocale]);

  const handleConnect = async () => {
    if (!appId || !apiKey.trim() || !projectId.trim()) return;
    setConnecting(true);
    try {
      await connectRevenueCat(appId, apiKey.trim(), projectId.trim());
      setApiKey('');
      setProjectId('');
      await refetch();
    } catch (e) {
      showError(e instanceof AppError ? e.userMessage : t('revenueCat.connectError'));
    } finally {
      setConnecting(false);
    }
  };

  const confirmDisconnect = async () => {
    if (!appId) return;
    setDisconnecting(true);
    try {
      await disconnectRevenueCat(appId);
      setDisconnectConfirmOpen(false);
      setPaywalls([]);
      setPaywall(null);
      setSelectedPaywallId(null);
      await refetch();
    } catch (e) {
      showError(e instanceof AppError ? e.userMessage : t('revenueCat.disconnectError'));
    } finally {
      setDisconnecting(false);
    }
  };

  const hasUnsavedChanges = useMemo(() => {
    if (!paywall || savedBaseline == null) return false;
    return serializeRevenueCatSaveState(paywall) !== savedBaseline;
  }, [paywall, savedBaseline]);

  const handleSyncNow = async () => {
    if (!appId) return;
    if (hasUnsavedChanges && !window.confirm(t('revenueCat.unsavedDiscardConfirm'))) return;
    setSyncing(true);
    try {
      await syncRevenueCatPaywalls(appId);
      await loadPaywalls();
      if (selectedPaywallId) await loadPaywallDetail(selectedPaywallId);
    } catch (e) {
      showError(e instanceof AppError ? e.userMessage : t('revenueCat.syncError'));
    } finally {
      setSyncing(false);
    }
  };

  const persistPaywall = useCallback(
    async (next: RevenueCatPaywall) => {
      if (!appId) return;
      const entryTranslations: Record<string, Record<string, string>> = {};
      const entryTranslate: Record<string, boolean> = {};
      for (const e of next.entries) {
        entryTranslations[e.key] = { ...(e.translations ?? {}) };
        entryTranslate[e.key] = e.translate !== false;
      }
      await saveRevenueCatPaywall(appId, next.id, {
        primaryLocale: next.primaryLocale,
        targetLocales: next.targetLocales,
        entryTranslations,
        entryTranslate,
      });
      setPaywall(next);
      setSavedBaseline(serializeRevenueCatSaveState(next));
    },
    [appId]
  );

  const handleSave = async () => {
    if (!paywall || !hasUnsavedChanges) return;
    setSaveStatus('loading');
    try {
      await persistPaywall(paywall);
      setSaveStatus('success');
      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch (e) {
      setSaveStatus('error');
      showError(e instanceof AppError ? e.userMessage : t('revenueCat.saveError'));
      setTimeout(() => setSaveStatus('idle'), 3000);
    }
  };

  const saveLocalesAndTranslate = async () => {
    if (!isPro) {
      goToUpgrade();
      return;
    }
    if (!appId || !paywall) return;
    setSavingLocales(true);
    try {
      const primary = isRevenueCatPaywallLocale(primaryLocaleDraft)
        ? primaryLocaleDraft
        : DEFAULT_APP_STORE_LOCALE;
      const targets = filterRevenueCatLocales(targetLocalesDraft).filter(l => l !== primary);
      const next: RevenueCatPaywall = {
        ...paywall,
        primaryLocale: primary,
        targetLocales: targets,
        completedTargetLocales: paywall.completedTargetLocales.filter(l => targets.includes(l)),
      };
      // Dil ayarı + bekleyen metin/translate değişikliklerini tek istekte kaydet, sonra çeviri.
      await persistPaywall(next);
      setPrimaryLocaleDraft(primary);
      setTargetLocalesDraft(targets);
      setCurrentEditingLocale(prev => (prev === paywall.primaryLocale ? primary : prev));
      setLocalesModalOpen(false);
      if (targets.length > 0) {
        const { jobId } = await createRevenueCatTranslateJob(appId, next.id);
        triggerJob(jobId).catch(er => showError(er instanceof Error ? er.message : String(er)));
        getJob(jobId)
          .then(job => setActiveTranslationJob(job))
          .catch(() =>
            setActiveTranslationJob({
              id: jobId,
              applicationId: appId,
              type: 'revenuecat_ai_translation',
              payload: {},
              status: 'pending',
              progress: 0,
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
            })
          );
      }
    } catch (e) {
      showError(e instanceof AppError ? e.userMessage : t('revenueCat.saveError'));
    } finally {
      setSavingLocales(false);
    }
  };

  const translationJobId = activeTranslationJob?.id;
  const translationJobStatus = activeTranslationJob?.status;
  useEffect(() => {
    if (!translationJobId || (translationJobStatus !== 'pending' && translationJobStatus !== 'processing')) return;
    const interval = setInterval(() => {
      getJob(translationJobId)
        .then(updated => setActiveTranslationJob(updated))
        .catch(() => {});
    }, 2000);
    return () => clearInterval(interval);
  }, [translationJobId, translationJobStatus]);

  useEffect(() => {
    const prev = translationJobPrevStatusRef.current;
    const curr = activeTranslationJob?.status ?? null;
    translationJobPrevStatusRef.current = curr;
    if (
      activeTranslationJob?.type === 'revenuecat_ai_translation' &&
      (curr === 'completed' || curr === 'failed') &&
      (prev === 'pending' || prev === 'processing') &&
      paywall
    ) {
      void loadPaywallDetail(paywall.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTranslationJob?.status, activeTranslationJob?.type]);

  const uploadJobId = activeUploadJob?.id;
  const uploadJobStatus = activeUploadJob?.status;
  useEffect(() => {
    if (!uploadJobId || (uploadJobStatus !== 'pending' && uploadJobStatus !== 'processing')) return;
    const interval = setInterval(() => {
      getJob(uploadJobId)
        .then(updated => setActiveUploadJob(updated))
        .catch(() => {});
    }, 2000);
    return () => clearInterval(interval);
  }, [uploadJobId, uploadJobStatus]);

  useEffect(() => {
    const prev = uploadJobPrevStatusRef.current;
    const curr = activeUploadJob?.status ?? null;
    uploadJobPrevStatusRef.current = curr;
    if (
      activeUploadJob?.type === 'revenuecat_upload' &&
      (curr === 'completed' || curr === 'failed') &&
      (prev === 'pending' || prev === 'processing')
    ) {
      if (curr === 'failed' && activeUploadJob.errorMessage) {
        showError(activeUploadJob.errorMessage);
      }
      if (paywall) void loadPaywallDetail(paywall.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeUploadJob?.status, activeUploadJob?.type]);

  const handleEntryTextChange = (key: string, value: string) => {
    if (!paywall || currentEditingLocale === paywall.primaryLocale) return;
    setEntryDrafts(prev => ({ ...prev, [key]: value }));
    setPaywall(prev =>
      prev
        ? {
            ...prev,
            entries: prev.entries.map(e =>
              e.key === key
                ? { ...e, translations: { ...e.translations, [currentEditingLocale]: value } }
                : e
            ),
          }
        : prev
    );
  };

  const handleToggleTranslate = (key: string, translate: boolean) => {
    setPaywall(prev =>
      prev
        ? {
            ...prev,
            entries: prev.entries.map(e => (e.key === key ? { ...e, translate } : e)),
          }
        : prev
    );
  };

  const handleUpload = async () => {
    if (!appId || !paywall) return;
    if (hasUnsavedChanges) {
      setSaveStatus('loading');
      try {
        await persistPaywall(paywall);
        setSaveStatus('idle');
      } catch (e) {
        setSaveStatus('error');
        showError(e instanceof AppError ? e.userMessage : t('revenueCat.saveError'));
        setTimeout(() => setSaveStatus('idle'), 3000);
        return;
      }
    }
    try {
      const { jobId } = await createRevenueCatUploadJob(appId, paywall.id);
      triggerJob(jobId).catch(er => showError(er instanceof Error ? er.message : String(er)));
      getJob(jobId)
        .then(job => setActiveUploadJob(job))
        .catch(() =>
          setActiveUploadJob({
            id: jobId,
            applicationId: appId,
            type: 'revenuecat_upload',
            payload: {},
            status: 'pending',
            progress: 0,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          })
        );
    } catch (e) {
      showError(e instanceof AppError ? e.userMessage : t('revenueCat.uploadError'));
    }
  };

  const selectPaywall = (nextId: string | null) => {
    if (nextId === selectedPaywallId) return;
    if (hasUnsavedChanges && !window.confirm(t('revenueCat.unsavedDiscardConfirm'))) return;
    setSelectedPaywallId(nextId);
  };

  const confirmDelete = async () => {
    if (!appId || !paywall) return;
    setDeleting(true);
    try {
      await deleteRevenueCatPaywall(appId, paywall.id);
      setDeleteConfirmOpen(false);
      setPaywall(null);
      setSelectedPaywallId(null);
      await loadPaywalls();
    } catch (e) {
      showError(e instanceof AppError ? e.userMessage : t('revenueCat.deleteError'));
    } finally {
      setDeleting(false);
    }
  };

  const preferredLocales = useMemo(
    () => (paywall ? [paywall.primaryLocale, ...paywall.targetLocales].filter(Boolean) : []),
    [paywall]
  );

  const isUploading = activeUploadJob?.status === 'pending' || activeUploadJob?.status === 'processing';
  const uploadDisabled =
    !paywall ||
    isUploading ||
    (paywall.targetLocales.length > 0 &&
      paywall.targetLocales.some(l => !paywall.completedTargetLocales.includes(l)));

  const isTranslating = activeTranslationJob?.status === 'pending' || activeTranslationJob?.status === 'processing';

  if (!appId) return null;

  return (
    <AppPageLayout app={app} loading={loading} error={error} pageTitle={t('revenueCat.pageTitle')}>
      {error && !app && (
        <div className="rounded-lg border border-amber-200 dark:border-amber-900/50 bg-amber-50 dark:bg-amber-900/20 px-4 py-3 text-sm text-amber-800 dark:text-amber-200">
          {error}
        </div>
      )}

      {app && !connected && (
        <div className="max-w-xl space-y-6">
          <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-5 shadow-sm">
            <div className="flex items-start gap-3">
              <CreditCard className="h-10 w-10 text-primary-600 dark:text-primary-400 flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {t('revenueCat.heading')}
                </h2>
                <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                  {t('revenueCat.description')}
                </p>
                <p className="mt-2 text-xs text-gray-500 dark:text-gray-500">
                  {t('revenueCat.connectHintPermissions')}
                </p>
              </div>
            </div>
            <div className="mt-5 space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('revenueCat.apiKeyLabel')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="password"
                  value={apiKey}
                  onChange={e => setApiKey(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono text-sm"
                  placeholder="sk_..."
                  autoComplete="off"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('revenueCat.projectIdLabel')}{' '}
                  <span className="text-gray-400 font-normal">({t('revenueCat.optional')})</span>
                </label>
                <input
                  type="text"
                  value={projectId}
                  onChange={e => setProjectId(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono text-sm"
                  placeholder="proj..."
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  {t('revenueCat.projectIdOptionalHint')}
                </p>
              </div>
              <button
                type="button"
                onClick={() => void handleConnect()}
                disabled={connecting || !apiKey.trim()}
                className="inline-flex items-center justify-center rounded-lg bg-primary-600 hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-600 text-white px-4 py-2.5 text-sm font-semibold shadow-sm disabled:opacity-50"
              >
                {connecting ? t('common.loading') : t('revenueCat.connect')}
              </button>
            </div>
          </div>
        </div>
      )}

      {app && connected && (
        <div className="space-y-4">
          <div className="flex gap-1 p-1 rounded-xl bg-gray-100 dark:bg-gray-800 w-fit">
            <button
              type="button"
              onClick={() => setActiveTab('catalog')}
              className={clsx(
                'h-8 px-3 rounded-lg text-sm font-medium transition-colors',
                activeTab === 'catalog'
                  ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
              )}
            >
              {t('revenueCat.tabCatalog')}
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('paywalls')}
              className={clsx(
                'h-8 px-3 rounded-lg text-sm font-medium transition-colors',
                activeTab === 'paywalls'
                  ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
              )}
            >
              {t('revenueCat.tabPaywalls')}
            </button>
          </div>

          {activeTab === 'catalog' && (
            <div className="space-y-4">
              <PageToolbar>
                <p className="text-sm text-gray-600 dark:text-gray-400 max-w-xl">
                  {t('revenueCat.catalogDescription')}
                </p>
                <div className="ml-auto flex flex-wrap items-center justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => void handleFullSetup()}
                    disabled={isFullSetupRunning || isProductSyncing || ensuringApps}
                    className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg text-sm font-semibold text-white bg-primary-600 hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-400 shadow-sm disabled:opacity-50"
                  >
                    <Upload className="h-4 w-4 shrink-0" />
                    <span className="hidden sm:inline">
                      {isFullSetupRunning ? t('revenueCat.fullSetupRunning') : t('revenueCat.fullSetup')}
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={() => void handleEnsureApps()}
                    disabled={ensuringApps || isProductSyncing || isFullSetupRunning || !app.revenueCatProjectId}
                    className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
                  >
                    <RotateCw className={clsx('h-3.5 w-3.5', ensuringApps && 'animate-spin')} />
                    <span className="hidden lg:inline">
                      {ensuringApps ? t('revenueCat.ensuringApps') : t('revenueCat.ensureApps')}
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={() => void handleSyncProducts()}
                    disabled={isProductSyncing || ensuringApps || isFullSetupRunning || !app.revenueCatProjectId}
                    className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
                  >
                    <span className="hidden lg:inline">
                      {isProductSyncing ? t('revenueCat.syncingProducts') : t('revenueCat.syncProducts')}
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setDisconnectConfirmOpen(true)}
                    className="text-xs text-gray-500 dark:text-gray-400 hover:underline px-1"
                  >
                    {t('revenueCat.disconnect')}
                  </button>
                </div>
              </PageToolbar>

              {isFullSetupRunning && activeFullSetupJob && (
                <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-3">
                  <div className="flex items-center justify-between text-sm mb-1.5">
                    <span className="font-medium text-gray-700 dark:text-gray-200">
                      {t('revenueCat.fullSetupRunning')}
                    </span>
                    <span className="text-gray-500 dark:text-gray-400">
                      %{activeFullSetupJob.progress ?? 0}
                    </span>
                  </div>
                  <div className="h-1.5 rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden">
                    <div
                      className="h-full bg-primary-500 transition-all"
                      style={{ width: `${activeFullSetupJob.progress ?? 0}%` }}
                    />
                  </div>
                </div>
              )}

              {(app.revenueCatProjectId || app.revenueCatEntitlementId || app.revenueCatOfferingId) && (
                <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 text-sm space-y-1">
                  <div className="font-medium text-gray-900 dark:text-white">{t('revenueCat.setupStatus')}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 font-mono break-all">
                    {app.revenueCatProjectId
                      ? t('revenueCat.projectIdValue', { id: app.revenueCatProjectId })
                      : t('revenueCat.projectMissing')}
                  </div>
                  {app.revenueCatEntitlementId && (
                    <div className="text-xs text-gray-500 dark:text-gray-400 font-mono break-all">
                      {t('revenueCat.entitlementIdValue', { id: app.revenueCatEntitlementId })}
                    </div>
                  )}
                  {app.revenueCatOfferingId && (
                    <div className="text-xs text-gray-500 dark:text-gray-400 font-mono break-all">
                      {t('revenueCat.offeringIdValue', { id: app.revenueCatOfferingId })}
                    </div>
                  )}
                  <div className="text-xs text-gray-500 dark:text-gray-400 pt-1">
                    {app.appStoreIapSecretId ? (
                      t('revenueCat.iapSecretLinked')
                    ) : (
                      <>
                        {t('revenueCat.iapSecretMissing')}{' '}
                        <Link
                          to={panelPath('/secrets')}
                          className="text-primary-600 dark:text-primary-400 hover:underline"
                        >
                          {t('nav.secrets')}
                        </Link>
                        {' · '}
                        <Link
                          to={panelPath(`/applications/${app.id}`)}
                          className="text-primary-600 dark:text-primary-400 hover:underline"
                        >
                          {t('revenueCat.iapSecretCredentialsLink')}
                        </Link>
                      </>
                    )}
                  </div>
                  {app.revenueCatSetupCompletedAt && (
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {t('revenueCat.setupCompletedAt', {
                        date: new Date(app.revenueCatSetupCompletedAt).toLocaleString(),
                      })}
                    </div>
                  )}
                </div>
              )}

              {isProductSyncing && activeProductSyncJob && (
                <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-3">
                  <div className="flex items-center justify-between text-sm mb-1.5">
                    <span className="font-medium text-gray-700 dark:text-gray-200">
                      {t('revenueCat.syncingProducts')}
                    </span>
                    <span className="text-gray-500 dark:text-gray-400">
                      %{activeProductSyncJob.progress ?? 0}
                    </span>
                  </div>
                  <div className="h-1.5 rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden">
                    <div
                      className="h-full bg-primary-500 transition-all"
                      style={{ width: `${activeProductSyncJob.progress ?? 0}%` }}
                    />
                  </div>
                </div>
              )}

              <div className="grid gap-3 sm:grid-cols-2 text-sm">
                <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 space-y-1">
                  <div className="font-medium text-gray-900 dark:text-white">{t('revenueCat.iosApp')}</div>
                  <div className="text-gray-500 dark:text-gray-400 font-mono text-xs break-all">
                    {catalog?.iosBundleId || app.iosBundleId || t('revenueCat.bundleMissing')}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {catalog?.iosAppId
                      ? t('revenueCat.rcAppId', { id: catalog.iosAppId })
                      : t('revenueCat.rcAppMissing')}
                  </div>
                </div>
                <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 space-y-1">
                  <div className="font-medium text-gray-900 dark:text-white">{t('revenueCat.androidApp')}</div>
                  <div className="text-gray-500 dark:text-gray-400 font-mono text-xs break-all">
                    {catalog?.androidBundleId || app.androidBundleId || t('revenueCat.bundleMissing')}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {catalog?.androidAppId
                      ? t('revenueCat.rcAppId', { id: catalog.androidAppId })
                      : t('revenueCat.rcAppMissing')}
                  </div>
                  <div className="text-xs text-amber-700 dark:text-amber-300">
                    {t('revenueCat.androidProductsLater')}
                  </div>
                </div>
              </div>

              {(catalog?.lastProductSyncAt || catalog?.lastProductSync) && (
                <p className="text-xs text-gray-500 dark:text-gray-400 px-1">
                  {catalog.lastProductSyncAt &&
                    t('revenueCat.lastProductSyncAt', {
                      date: new Date(catalog.lastProductSyncAt).toLocaleString(),
                    })}
                  {catalog.lastProductSync &&
                    ` · ${t('revenueCat.productSyncSummary', {
                      created: catalog.lastProductSync.created,
                      skipped: catalog.lastProductSync.skipped,
                      failed: catalog.lastProductSync.failed,
                    })}`}
                  {catalog.lastProductSync &&
                    (catalog.lastProductSync.packagesLinked ?? 0) > 0 &&
                    ` · ${t('revenueCat.productSyncPackagesLinked', {
                      count: catalog.lastProductSync.packagesLinked,
                    })}`}
                </p>
              )}

              <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden">
                <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700">
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                    {t('revenueCat.iosProductsTitle')}
                  </h3>
                </div>
                {catalogLoading && !catalog ? (
                  <div className="p-6 text-center text-sm text-gray-500">{t('common.loading')}</div>
                ) : !catalog?.iosProducts?.length ? (
                  <div className="p-6 text-center text-sm text-gray-500 dark:text-gray-400">
                    {t('revenueCat.noIosProductsHint')}
                  </div>
                ) : (
                  <ul className="divide-y divide-gray-100 dark:divide-gray-700">
                    {catalog.iosProducts.map(row => (
                      <li
                        key={row.productId}
                        className="px-4 py-3 flex flex-wrap items-center gap-2 justify-between text-sm"
                      >
                        <div className="min-w-0">
                          <div className="font-mono text-gray-900 dark:text-white truncate">
                            {row.productId}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            {row.displayName || row.type}
                            {row.subscriptionDuration ? ` · ${row.subscriptionDuration}` : ''}
                          </div>
                        </div>
                        <span
                          className={clsx(
                            'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
                            row.status === 'synced'
                              ? 'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                              : 'bg-amber-50 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300'
                          )}
                        >
                          {row.status === 'synced'
                            ? t('revenueCat.productStatusSynced')
                            : t('revenueCat.productStatusMissing')}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}

          {activeTab === 'paywalls' && (
        <div className="space-y-4">
          <PageToolbar>
            <select
              value={selectedPaywallId ?? ''}
              onChange={e => selectPaywall(e.target.value || null)}
              className="h-8 pl-2 pr-6 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100 text-sm"
            >
              {paywalls.length === 0 && <option value="">{t('revenueCat.noPaywalls')}</option>}
              {paywalls.map(p => (
                <option key={p.id} value={p.id}>
                  {p.name || p.remotePaywallId} ({p.completedTargetLocales.length}/{p.targetLocales.length})
                </option>
              ))}
            </select>

            <button
              type="button"
              onClick={() => void handleSyncNow()}
              disabled={syncing}
              className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
            >
              <RotateCw className={clsx('h-3.5 w-3.5', syncing && 'animate-spin')} />
              <span className="hidden sm:inline">{syncing ? t('revenueCat.syncing') : t('revenueCat.syncNow')}</span>
            </button>

            {paywall && (
              <LocaleSelectMenu
                id="revenuecat-locale"
                value={currentEditingLocale}
                onChange={setCurrentEditingLocale}
                locales={REVENUECAT_PAYWALL_LOCALIZATIONS}
                preferredLocales={preferredLocales}
                primaryLocale={paywall.primaryLocale}
              />
            )}

            <div className="ml-auto flex flex-wrap items-center justify-end gap-2">
              {paywall && (
                <>
                  {/* Utility */}
                  <button
                    type="button"
                    onClick={() => setDeleteConfirmOpen(true)}
                    className="inline-flex items-center justify-center h-8 w-8 rounded-lg border border-transparent text-gray-500 dark:text-gray-400 hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-600 hover:border-red-200 dark:hover:border-red-800 transition-colors"
                    title={t('common.delete')}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>

                  <div className="hidden sm:block w-px h-6 bg-gray-200 dark:bg-gray-600 shrink-0" aria-hidden />

                  {/* AI tools */}
                  <div className="flex items-center gap-1 rounded-xl p-1 bg-primary-50 dark:bg-primary-950/50 ring-1 ring-inset ring-primary-200/90 dark:ring-primary-800/70">
                    <button
                      type="button"
                      onClick={() => {
                        if (!isPro) {
                          goToUpgrade();
                          return;
                        }
                        setLocalesModalOpen(true);
                      }}
                      title={isPro ? t('revenueCat.aiTranslation') : t('proFeature.requiresPro')}
                      className={clsx(
                        'inline-flex items-center justify-center gap-1.5 h-8 px-3 rounded-lg text-sm font-semibold transition-colors',
                        isPro
                          ? 'bg-primary-600 text-white shadow-sm hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-400'
                          : 'bg-white/70 dark:bg-gray-800/60 text-gray-500 dark:text-gray-400 hover:bg-white dark:hover:bg-gray-800 cursor-pointer'
                      )}
                    >
                      <Languages className="h-4 w-4 shrink-0" />
                      <span className="hidden sm:inline">{t('revenueCat.aiTranslation')}</span>
                      {!isPro && <ProTag />}
                    </button>
                  </div>

                  <div className="hidden sm:block w-px h-6 bg-gray-200 dark:bg-gray-600 shrink-0" aria-hidden />

                  {/* Publish: Upload (secondary) then Save (primary) */}
                  <button
                    type="button"
                    onClick={() => void handleUpload()}
                    disabled={uploadDisabled || saveStatus === 'loading'}
                    title={
                      uploadDisabled
                        ? t('revenueCat.translationsIncomplete')
                        : hasUnsavedChanges
                          ? t('revenueCat.uploadSavesFirst')
                          : undefined
                    }
                    className="inline-flex items-center justify-center gap-1.5 h-8 px-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600 text-sm font-medium transition-colors disabled:opacity-40 disabled:pointer-events-none"
                  >
                    <Upload className="h-4 w-4 shrink-0" />
                    <span className="hidden lg:inline">
                      {isUploading ? t('revenueCat.uploading') : t('revenueCat.uploadToRevenueCat')}
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={() => void handleSave()}
                    disabled={!hasUnsavedChanges || saveStatus === 'loading'}
                    title={!hasUnsavedChanges ? t('revenueCat.noUnsavedChanges') : undefined}
                    className={clsx(
                      'inline-flex items-center justify-center gap-1.5 h-8 px-3 rounded-lg text-sm font-semibold transition-colors disabled:opacity-50 disabled:pointer-events-none',
                      saveStatus === 'success'
                        ? 'border border-green-500 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300'
                        : saveStatus === 'error'
                          ? 'border border-red-500 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300'
                          : 'text-white bg-primary-600 hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-400 shadow-sm'
                    )}
                  >
                    <Save className="h-4 w-4 shrink-0" />
                    <span className="hidden lg:inline">
                      {saveStatus === 'loading'
                        ? t('panel.saving')
                        : saveStatus === 'success'
                          ? t('appScreen.saved')
                          : t('common.save')}
                    </span>
                  </button>
                </>
              )}
              <button
                type="button"
                onClick={() => setDisconnectConfirmOpen(true)}
                className="text-xs text-gray-500 dark:text-gray-400 hover:underline px-1"
              >
                {t('revenueCat.disconnect')}
              </button>
            </div>
          </PageToolbar>

          {paywall && (
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500 dark:text-gray-400 px-1">
              {paywall.autoManaged && (
                <span className="inline-flex items-center gap-1 rounded-full bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 px-2 py-0.5 font-medium">
                  {t('revenueCat.autoSyncedBadge')}
                </span>
              )}
              <span>
                {paywall.lastSyncedAt
                  ? t('revenueCat.lastSyncedAt', { date: new Date(paywall.lastSyncedAt).toLocaleString() })
                  : t('revenueCat.neverSynced')}
              </span>
              <span>
                {t('revenueCat.localeCoverage', {
                  done: paywall.completedTargetLocales.length,
                  total: paywall.targetLocales.length,
                })}
              </span>
            </div>
          )}

          {isTranslating && activeTranslationJob && (
            <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-3">
              <div className="flex items-center justify-between text-sm mb-1.5">
                <span className="font-medium text-gray-700 dark:text-gray-200">
                  {t('revenueCat.translating')}
                </span>
                <span className="text-gray-500 dark:text-gray-400">%{activeTranslationJob.progress ?? 0}</span>
              </div>
              <div className="h-1.5 rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden">
                <div
                  className="h-full bg-primary-500 transition-all"
                  style={{ width: `${activeTranslationJob.progress ?? 0}%` }}
                />
              </div>
            </div>
          )}

          {isUploading && activeUploadJob && (
            <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-3">
              <div className="flex items-center justify-between text-sm mb-1.5">
                <span className="font-medium text-gray-700 dark:text-gray-200">
                  {t('revenueCat.uploading')}
                </span>
                <span className="text-gray-500 dark:text-gray-400">%{activeUploadJob.progress ?? 0}</span>
              </div>
              <div className="h-1.5 rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden">
                <div
                  className="h-full bg-primary-500 transition-all"
                  style={{ width: `${activeUploadJob.progress ?? 0}%` }}
                />
              </div>
            </div>
          )}

          {!paywall && !loadingPaywall && (
            <div className="rounded-xl border border-dashed border-gray-300 dark:border-gray-600 p-8 text-center text-sm text-gray-500 dark:text-gray-400">
              {t('revenueCat.syncEmpty')}
            </div>
          )}

          {paywall && (
            <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 divide-y divide-gray-100 dark:divide-gray-700">
              {paywall.entries.length === 0 && (
                <div className="p-6 text-center text-sm text-gray-500 dark:text-gray-400">
                  {t('revenueCat.noEntries')}
                </div>
              )}
              {paywall.entries.map(entry => {
                const translate = entry.translate !== false;
                return (
                  <div key={entry.key} className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <div className="text-xs font-mono text-gray-400 dark:text-gray-500 truncate">
                          {entry.key}
                        </div>
                        <label className="inline-flex items-center gap-1.5 shrink-0 text-xs text-gray-500 dark:text-gray-400 cursor-pointer select-none">
                          <input
                            type="checkbox"
                            checked={translate}
                            onChange={e => handleToggleTranslate(entry.key, e.target.checked)}
                            className="rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500"
                          />
                          {t('revenueCat.translateEntry')}
                        </label>
                      </div>
                      <div className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                        {entry.sourceText}
                      </div>
                      {!translate && (
                        <div className="mt-1 text-xs text-gray-400 dark:text-gray-500">
                          {t('revenueCat.translateEntryOffHint')}
                        </div>
                      )}
                    </div>
                    <div>
                      <textarea
                        value={
                          !translate && currentEditingLocale !== paywall.primaryLocale
                            ? entry.sourceText
                            : (entryDrafts[entry.key] ?? '')
                        }
                        disabled={
                          currentEditingLocale === paywall.primaryLocale || !translate
                        }
                        onChange={e => handleEntryTextChange(entry.key, e.target.value)}
                        rows={2}
                        className="w-full px-2.5 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm disabled:opacity-60 disabled:bg-gray-50 dark:disabled:bg-gray-800"
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
          )}
        </div>
      )}

      <ModalLayout
        open={localesModalOpen}
        onClose={() => setLocalesModalOpen(false)}
        title={t('revenueCat.aiTranslationModalTitle')}
        description={t('revenueCat.aiTranslationModalDescription')}
        primaryLabel={savingLocales ? t('panel.saving') : t('common.save')}
        onPrimary={() => void saveLocalesAndTranslate()}
        primaryLoading={savingLocales}
        closeDisabled={savingLocales}
      >
        <SourceTargetLocalePicker
          sourceLocale={primaryLocaleDraft}
          onSourceChange={setPrimaryLocaleDraft}
          targetLocales={targetLocalesDraft}
          onTargetLocalesChange={setTargetLocalesDraft}
          locales={REVENUECAT_PAYWALL_LOCALIZATIONS}
          disabled={savingLocales}
          targetListClassName="max-h-56 overflow-y-auto"
        />
      </ModalLayout>

      <ConfirmDialog
        open={disconnectConfirmOpen}
        onClose={() => {
          if (!disconnecting) setDisconnectConfirmOpen(false);
        }}
        title={t('revenueCat.disconnectTitle')}
        message={t('revenueCat.disconnectConfirm')}
        confirmLabel={disconnecting ? t('common.loading') : t('revenueCat.disconnect')}
        cancelLabel={t('common.cancel')}
        onConfirm={() => void confirmDisconnect()}
        confirmLoading={disconnecting}
        closeDisabled={disconnecting}
        variant="danger"
      />

      <ConfirmDialog
        open={deleteConfirmOpen}
        onClose={() => {
          if (!deleting) setDeleteConfirmOpen(false);
        }}
        title={t('revenueCat.deleteTitle')}
        message={t('revenueCat.deleteConfirm')}
        confirmLabel={deleting ? t('common.loading') : t('common.delete')}
        cancelLabel={t('common.cancel')}
        onConfirm={() => void confirmDelete()}
        confirmLoading={deleting}
        closeDisabled={deleting}
        variant="danger"
      />
    </AppPageLayout>
  );
}
